// ============================================================================
// limitadorConcorrencia: fila com prioridade e teto ADAPTATIVO de chamadas ao
// mesmo tempo (A2b do "Dashboard sem zero silencioso", 23/10/2026).
// ============================================================================
// Evidência do próprio repositório: o Avec responde 403 com 5 consultas ao
// mesmo tempo (api/_lib/atendimento-avec.js: MAX_SIMULTANEAS = 3;
// api/_lib/fin-avec-coleta.js: "403 do Avec = limite de consultas simultâneas").
// Por isso o teto começa em no máximo 3; cada recusa por limite (403/429) baixa
// o teto (até `min`) e uma sequência de sucessos sobe devagar (1 por vez).
//
// executar(prioridade, fn, { deadline }) → Promise do resultado de fn().
//   prioridade: número (0 = mais alta) ou função () => número (lida a cada
//   ordenação; o dedupe em voo sobe a prioridade de um pedido já na fila).
//   deadline (ms absolutos do relógio): se o item ainda estiver na fila quando
//   o prazo vencer, a promessa rejeita com { motivo: 'prazo' }.
// pausar(ms): nenhuma saída nova da fila até lá.
// falhaDeTaxa() / sucesso(): ajustam o teto.
// rejeitarFila(pred, motivo): tira da fila (sem rodar) o que pred aceitar.
// Relógio injetável { agora, agendar, cancelar } (testes sem tempo real).
// ============================================================================

export function erroDaFila(motivo) {
  const e = new Error(`fila do Avec: ${motivo}`);
  e.motivo = motivo;
  e.transitoria = true;
  return e;
}

export function criarLimitador({ max = 3, min = 1, sucessosParaSubir = 10, relogio } = {}) {
  const rel = relogio || { agora: () => Date.now(), agendar: (fn, ms) => setTimeout(fn, ms), cancelar: (id) => clearTimeout(id) };
  const teto0 = Math.max(1, Math.floor(max));
  const piso = Math.max(1, Math.min(teto0, Math.floor(min)));
  let teto = teto0;
  let emCurso = 0;
  let maxEmCursoVisto = 0;
  let seq = 0;
  let sucessos = 0;
  let pausadoAte = 0;
  let timerAcordar = null;
  const fila = [];

  const prioridadeDe = (it) => {
    const p = typeof it.prioridade === 'function' ? it.prioridade() : it.prioridade;
    return Number.isFinite(p) ? p : 1;
  };
  const ordenar = () => fila.sort((a, b) => prioridadeDe(a) - prioridadeDe(b) || a.seq - b.seq);

  function agendarAcordar() {
    if (timerAcordar != null) return;
    const ms = Math.max(0, pausadoAte - rel.agora());
    timerAcordar = rel.agendar(() => { timerAcordar = null; bombear(); }, ms);
  }

  function bombear() {
    if (rel.agora() < pausadoAte) { if (fila.length) agendarAcordar(); return; }
    ordenar();
    while (emCurso < teto && fila.length) {
      const it = fila.shift();
      if (it.timerPrazo != null) { rel.cancelar(it.timerPrazo); it.timerPrazo = null; }
      if (it.deadline != null && rel.agora() >= it.deadline) { it.reject(erroDaFila('prazo')); continue; }
      emCurso++;
      if (emCurso > maxEmCursoVisto) maxEmCursoVisto = emCurso;
      let p;
      try { p = Promise.resolve(it.fn()); } catch (e) { p = Promise.reject(e); }
      p.then(
        (v) => { emCurso--; it.resolve(v); bombear(); },
        (e) => { emCurso--; it.reject(e); bombear(); },
      );
    }
  }

  return {
    executar(prioridade, fn, { deadline = null } = {}) {
      return new Promise((resolve, reject) => {
        const it = { prioridade, fn, resolve, reject, deadline, seq: ++seq, timerPrazo: null };
        if (deadline != null) {
          const ms = deadline - rel.agora();
          if (ms <= 0) { reject(erroDaFila('prazo')); return; }
          it.timerPrazo = rel.agendar(() => {
            it.timerPrazo = null;
            const i = fila.indexOf(it);
            if (i >= 0) { fila.splice(i, 1); reject(erroDaFila('prazo')); }
          }, ms);
        }
        fila.push(it);
        bombear();
      });
    },
    reordenar() { ordenar(); bombear(); },
    pausar(ms) {
      const ate = rel.agora() + Math.max(0, ms);
      if (ate > pausadoAte) {
        pausadoAte = ate;
        if (timerAcordar != null) { rel.cancelar(timerAcordar); timerAcordar = null; }
      }
      if (fila.length) agendarAcordar();
    },
    falhaDeTaxa() { teto = Math.max(piso, teto - 1); sucessos = 0; },
    sucesso() {
      sucessos++;
      if (sucessos >= sucessosParaSubir && teto < teto0) { teto++; sucessos = 0; bombear(); }
    },
    rejeitarFila(pred, motivo = 'disjuntor') {
      let n = 0;
      for (let i = fila.length - 1; i >= 0; i--) {
        const it = fila[i];
        if (!pred({ prioridade: prioridadeDe(it) })) continue;
        fila.splice(i, 1);
        if (it.timerPrazo != null) { rel.cancelar(it.timerPrazo); it.timerPrazo = null; }
        it.reject(erroDaFila(motivo));
        n++;
      }
      return n;
    },
    estado() {
      const porPrioridade = {};
      for (const it of fila) { const p = prioridadeDe(it); porPrioridade[p] = (porPrioridade[p] || 0) + 1; }
      return { emCurso, teto, tetoInicial: teto0, naFila: fila.length, porPrioridade, pausadoAte, maxEmCursoVisto };
    },
  };
}
